import type { ReactNode } from "react";
import { Text } from "../../../../atoms/Text";

export interface LedgerHoverCardProps {
  title: ReactNode;
  /** Quiet word after the title: "Labour item", "Deduction", a work type. */
  kind?: ReactNode;
  /** Free text under the heading — where a demoted description reappears. */
  description?: ReactNode;
  /** The facts, usually a list of `<Fact>` nodes. */
  children?: ReactNode;
  onOpenDetails?: () => void;
  openDetailsLabel?: string;
}

/**
 * The row hover card: a shortcut to the side panel. Truncated and demoted
 * facts reappear here, so which facts exist is entirely the caller's call.
 *
 * Rendered inside the DS `HoverCardContent` panel (LedgerRowText hosts it);
 * the panel owns the width bounds, border and elevation.
 */
export const LedgerHoverCard = ({
  title,
  kind,
  description,
  children,
  onOpenDetails,
  openDetailsLabel = "Open details",
}: LedgerHoverCardProps) => (
  <div>
    <div className="flex items-baseline gap-2">
      <Text
        as="span"
        variant="inherit"
        className="min-w-0 font-semibold text-(length:--ledger-text-title) leading-[1.43]"
      >
        {title}
      </Text>
      {kind && (
        <Text
          as="span"
          variant="inherit"
          color="secondary"
          className="shrink-0 text-(length:--ledger-text-cell) leading-[1.66]"
        >
          {kind}
        </Text>
      )}
    </div>
    {description && (
      <Text
        as="div"
        variant="inherit"
        color="secondary"
        className="mb-1.5 text-(length:--ledger-text-cell) leading-[1.66]"
      >
        {description}
      </Text>
    )}
    {children}
    {onOpenDetails && (
      <button
        type="button"
        onClick={onOpenDetails}
        className="mt-1.5 block cursor-pointer border-0 bg-transparent p-0 text-left text-(length:--ledger-text-cell) leading-[1.66] text-content-accent hover:underline"
      >
        {openDetailsLabel}
      </button>
    )}
  </div>
);
